import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { buildStepContent } from '../block-builder';
import type { GuideInteractionMetadata, GuideRenderContext, GuideStep } from '../types';
import { waitForElement } from '../wait-for-element';
import { BaseRenderer } from './base-renderer';
import { positionArrow } from './floating-arrow';

/**
 * Renderiza una guía tipo `tooltip` anclada a un elemento del host.
 * Espera el ancla con MutationObserver (selector puede no existir aún
 * en SPAs), la posiciona con Floating UI y la re-posiciona en scroll
 * y resize.
 *
 * Si el ancla no aparece dentro del timeout, no muestra nada. Esto es
 * deliberado: las guías "fantasma" sin destino son peor UX que no
 * mostrar.
 */
export class TooltipRenderer extends BaseRenderer {
  /** Lado preferido (mutable: el update in-place puede cambiarlo). */
  private side: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
  private reposition: (() => void) | null = null;

  async render(context: GuideRenderContext): Promise<void> {
    const step = context.guide.guideSteps[0];
    if (!step) return;

    const selector = step.selector ?? context.guide.activationRules.selector;
    if (typeof selector !== 'string' || selector.length === 0) return;

    const anchor = await waitForElement(selector);
    if (!anchor) return;

    // Lado preferido del tooltip respecto al ancla (arriba/abajo/izq/der).
    // `flip()` igual lo reubica si no entra; default `bottom`.
    const rawSide = (step.style as Record<string, unknown> | null | undefined)?.tooltipPlacement;
    this.side = rawSide === 'top' || rawSide === 'left' || rawSide === 'right' ? rawSide : 'bottom';

    const { root } = this.createHost();
    this.applyDesign(step.style);
    const ownerDocument = root.ownerDocument ?? document;

    const tooltip = ownerDocument.createElement('div');
    tooltip.className = 'veo-tooltip';

    const dismiss = (
      action: 'dismissed' | 'cta_clicked',
      ctaUrl?: string,
      meta?: GuideInteractionMetadata,
    ) => {
      context.onInteraction({
        guideId: context.guide.guideId,
        stepIndex: 0,
        action,
        ...(meta ? { metadata: meta } : {}),
      });
      if (ctaUrl) window.open(ctaUrl, '_blank', 'noopener,noreferrer');
      context.onClose();
    };

    const content = buildStepContent(step, ownerDocument, {
      onCtaClick: (action, target, meta) => {
        dismiss('cta_clicked', action === 'url' ? target : undefined, meta);
        if (action === 'guide' && target) context.onOpenGuide?.(target);
      },
      onDismiss: () => dismiss('dismissed'),
    });

    tooltip.appendChild(content);

    // Caret que apunta al elemento anclado (se reposiciona junto al tooltip).
    const arrowEl = ownerDocument.createElement('div');
    arrowEl.className = 'veo-tooltip-arrow';
    tooltip.appendChild(arrowEl);

    root.appendChild(tooltip);

    const updatePosition = async (): Promise<void> => {
      const { x, y, placement, middlewareData } = await computePosition(anchor, tooltip, {
        placement: this.side,
        middleware: [offset(10), flip(), shift({ padding: 8 }), arrow({ element: arrowEl })],
      });
      tooltip.style.left = `${x}px`;
      tooltip.style.top = `${y}px`;
      positionArrow(arrowEl, placement, middlewareData.arrow);
    };

    await updatePosition();

    const reposition = (): void => {
      void updatePosition();
    };
    this.reposition = reposition;
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    this.registerCleanup(() => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    });

    // Update in-place (preview del editor): mismo ancla → solo cambia contenido.
    this.liveContainer = tooltip;
    this.liveContent = content;
    this.liveKey = selector;
    this.liveBuild = (s) =>
      buildStepContent(s, ownerDocument, {
        onCtaClick: (action, target, meta) => {
          dismiss('cta_clicked', action === 'url' ? target : undefined, meta);
          if (action === 'guide' && target) context.onOpenGuide?.(target);
        },
        onDismiss: () => dismiss('dismissed'),
      });

    context.onInteraction({
      guideId: context.guide.guideId,
      stepIndex: 0,
      action: 'shown',
    });
  }

  protected override liveKeyOf(step: GuideStep): string {
    return step.selector ?? '';
  }

  protected override onLiveUpdate(step: GuideStep): void {
    const raw = (step.style as Record<string, unknown> | null | undefined)?.tooltipPlacement;
    this.side = raw === 'top' || raw === 'left' || raw === 'right' ? raw : 'bottom';
    this.reposition?.();
  }
}
